import { Validator } from "jsonschema";
import {
  getDistrictNameWithStateAndDistrictCode,
  getShortGender,
  getStateNameWithStateCode,
  stateCodeWithDistricts,
  stateOptionsWithCode
} from "./constants";

Validator.prototype.customFormats.userId = (input) =>
  /^(?!\.\.?$)(?!.*__.*__)([^/]{1,1500})$/.test(input);

Validator.prototype.customFormats.name = (input) => {
  const name = input.split(" ");
  for (const i of name) {
    if (i && !/^[a-zA-Z]+$/.test(i)) {
      return false;
    }
  }
  return input.length > 2;
};

Validator.prototype.customFormats.documentId = (input) =>
  /^(?!\.\.?$)(?!.*__.*__)([^/]{1,1500})$/.test(input);

Validator.prototype.customFormats.bucketUrl = (input) =>
  input.includes("gs://") || input === "";

Validator.prototype.customFormats.gender = (input) =>
  ["male", "female", "other"].includes(input);

Validator.prototype.customFormats.registrationYear = (input) => input > 1950;

Validator.prototype.customFormats.bloodGroup = (input) =>
  [
    "A +ve",
    "A -ve",
    "B +ve",
    "B -ve",
    "O +ve",
    "O +ve",
    "O -ve",
    "AB +ve",
    "AB -ve"
  ].includes(input);

Validator.prototype.customFormats.timestamp = (input) =>
  new Date("01 Jan 1920") < new Date(input) < new Date();

Validator.prototype.customFormats.documentType = (input) =>
  ["prescription", "report", "symptom"].includes(input);

Validator.prototype.customFormats.userType = (input) =>
  ["doctor", "patient", "clinic"].includes(input);

Validator.prototype.customFormats.fitnessType = (input) =>
  input === "googleFit";

const v = new Validator();

Validator.prototype.customFormats.timestamp = (input) =>
  new Date("01 Jan 1920") < new Date(input) < new Date();

Validator.prototype.customFormats.userId = (input) =>
  /^(?!\.\.?$)(?!.*__.*__)([^/]{1,1500})$/.test(input);

const vitalsModel = (profile, method) => {
  const vitalsSchema = {
    id: "/patientProfile",
    type: "object",
    properties: {
      height: { type: "number" },
      weight: { type: "number", minimum: 3 },
      to: {
        type: "object",
        properties: {
          fullName: { type: "string" },
          id: { type: "string", format: "documentId" },
          phoneNumber: { type: "string", format: "phone-number" }
        }
      },
      from: {
        type: "object",
        properties: {
          id: { type: "string", format: "documentId" },
          name: { type: "string", format: "documentId" },
          type: { type: "string", format: "userType" }
        }
      },
      timestamp: { type: "number", format: "timestamp" },
      heartRate: { type: "number" },
      bodyTemperature: { type: "number" },
      bloodOxygen: { type: "number" },
      bloodSugar: { type: "number" },
      bloodPressure: { type: "string" }
    },
    ...(method === "create" ? { required: ["timestamp", "to", "from"] } : {})
  };
  function preValidateProperty(object, key, schema, options, ctx) {
    const required =
      method === "create" ? ["timestamp", "to", "from", "phoneNumber"] : [];
    const value = object[key];
    if (typeof value === "undefined" && required.includes(key)) {
      throw new Error(`Undefined value at : ${key}`);
    }
    if (key !== "fullName") {
      return;
    }
    const name = value.split(" ");
    let result = "";
    for (const i of name) {
      if (i) result += " " + i;
    }
    object[key] = result.trim();
  }
  function rewriteQuery(data) {
    return {
      processing: true,
      ...(data.heartRate
        ? {
            heartRate: {
              value: data.heartRate,
              status: ""
            }
          }
        : {}),
      ...(data.bodyTemperature
        ? {
            bodyTemperature: {
              value: data.bodyTemperature,
              status: ""
            }
          }
        : {}),
      ...(data.bloodOxygen
        ? {
            bloodOxygen: {
              value: data.bloodOxygen,
              status: ""
            }
          }
        : {}),
      ...(data.bloodSugar
        ? {
            bloodSugar: {
              value: data.bloodSugar,
              status: ""
            }
          }
        : {}),
      ...(data.bloodPressure
        ? {
            bloodPressure: {
              value: data.bloodPressure,
              status: ""
            }
          }
        : {}),
      type: "vitals",
      ...(data.timestamp ? { timestamp: data.timestamp } : {}),
      ...(data.to ? { to: data.to } : {}),
      ...(data.from ? { from: data.from } : {}),
      ...(data.weight ? { weight: data.weight } : {}),
      ...(data.height ? { height: data.height } : {})
    };
  }

  v.validate(profile, vitalsSchema, {
    throwAll: true,
    preValidateProperty
  });
  return rewriteQuery(profile);
};

const documentModel = (profile, method) => {
  const documentCollectionSchema = {
    id: "/document",
    type: "object",
    properties: {
      from: {
        type: "object",
        properties: {
          id: { type: "string", format: "documentId" },
          name: { type: "string" },
          type: { type: "string", format: "userType" }
        }
      },
      processing: { type: "boolean" },
      type: { type: "string", format: "documentType" },
      url: { type: "string", format: "bucketUrl" },
      notes: {
        type: "object",
        properties: {
          "^[a-zA-Z0-9_.-]*$": {
            type: "object",
            properties: {
              pages: { type: "array", items: { type: "integer" } },
              authorName: { type: "string", format: "name" },
              isDraft: { type: "boolean" }
            }
          }
        }
      },
      timestamp: { type: "integer", format: "timestamp" },
      to: {
        type: "object",
        properties: {
          fullName: { type: "string", format: "name" },
          id: { type: "string", format: "documentId" },
          phoneNumber: { type: "string", format: "phone-number" }
        }
      }
    },
    ...(method === "create"
      ? {
          required: ["from", "processing", "type", "url", "timestamp", "to"]
        }
      : {})
  };
  function preValidateProperty(object, key, schema, options, ctx) {
    const required =
      method === "create"
        ? ["from", "processing", "type", "url", "timestamp", "to"]
        : [];
    const value = object[key];
    if (typeof value === "undefined" && required.includes(key)) {
      throw new Error(`Undefined value at : ${key}`);
    }
  }
  function rewriteDocuments(data) {
    const result = {
      ...(data.from ? { from: data.from } : {}),
      ...(data.processing ? { processing: data.processing } : {}),
      ...(data.type ? { type: data.type } : {}),
      ...(data.url ? { url: data.url } : {}),
      ...(data.notes ? { notes: data.notes } : {}),
      ...(data.timestamp ? { timestamp: data.timestamp } : {}),
      ...(data.to ? { to: data.to } : {})
    };
    return result;
  }
  v.validate(profile, documentCollectionSchema, {
    throwAll: true,
    preValidateProperty
  });
  return rewriteDocuments(profile);
};

const fitnessModel = (profile, method) => {
  const fitnessSchema = {
    id: "/patientProfile",
    type: "object",
    properties: {
      to: {
        type: "object",
        properties: {
          fullName: { type: "string" },
          id: { type: "string", format: "documentId" },
          phoneNumber: { type: "string", format: "phone-number" }
        }
      },
      from: {
        type: "object",
        properties: {
          id: { type: "string", format: "documentId" },
          name: { type: "string", format: "documentId" },
          type: { type: "string", format: "fitnessType" }
        }
      },
      timestamp: { type: "number", format: "timestamp" },
      stepCount: { type: "number" },
      heartPoints: { type: "number" },
      caloriesBurned: { type: "number" },
      moveMinutes: { type: "number" }
    },
    ...(method === "create" ? { required: ["timestamp", "to", "from"] } : {})
  };

  function preValidateProperty(object, key, schema, options, ctx) {
    const required = method === "create" ? ["timestamp", "to", "from"] : [];
    const value = object[key];
    if (typeof value === "undefined" && required.includes(key)) {
      throw new Error(`Undefined value at : ${key}`);
    }
    if (key !== "fullName") {
      return;
    }
    const name = value.split(" ");
    let result = "";
    for (const i of name) {
      if (i) result += ` ${i}`;
    }
    object[key] = result.trim();
  }

  function rewriteQuery(data) {
    const result = {
      type: "vitals",
      ...(data.timestamp ? { timestamp: data.timestamp } : {}),
      ...(data.to ? { to: data.to } : {}),
      ...(data.from ? { from: data.from } : {}),
      ...(data.type ? { type: data.type } : {}),
      ...(data.stepCount ? { stepCount: data.stepCount } : {}),
      ...(data.heartPoints ? { heartPoints: data.heartPoints } : {}),
      ...(data.caloriesBurned ? { caloriesBurned: data.caloriesBurned } : {}),
      ...(data.moveMinutes ? { moveMinutes: data.moveMinutes } : {})
    };
    return result;
  }

  v.validate(profile, fitnessSchema, {
    throwAll: true,
    preValidateProperty
  });
  return rewriteQuery(profile);
};
const symptomsModel = (profile, method) => {
  const symptomsCollectionSchema = {
    id: "/symptom",
    type: "object",
    properties: {
      pid: { type: "string", format: "documentId" },
      fullName: { type: "string" },
      description: { type: "string" },
      url: {
        type: "object",
        properties: {
          audio: { type: "string", format: "bucketUrl" },
          images: {
            type: "array",
            items: {
              type: "string",
              format: "bucketUrl"
            }
          }
        }
      },
      severityScale: { type: "number" },
      type: { type: "string", format: "documentType" },
      location: {
        type: "object",
        properties: {
          front: {
            type: "array",
            items: {
              type: "object",
              properties: {
                posx: { type: "number" },
                posX: { type: "number" }
              }
            }
          },
          back: {
            type: "array",
            items: {
              type: "object",
              properties: {
                posx: { type: "number" },
                posY: { type: "number" }
              }
            }
          }
        }
      },
      phoneNumber: { type: "string", format: "phone-number" },
      timestamp: { type: "integer", format: "timestamp" }
    },
    ...(method === "create"
      ? {
          required: ["pid", "severityScale", "timestamp", "type", "fullName"]
        }
      : {})
  };
  function preValidateProperty(object, key, schema, options, ctx) {
    const required =
      method === "create"
        ? ["pid", "severityScale", "timestamp", "type", "fullName"]
        : [];
    const value = object[key];
    if (typeof value === "undefined" && required.includes(key)) {
      throw new Error(`Undefined value at : ${key}`);
    }
  }
  function rewriteSymptoms(data) {
    return {
      ...(data.pid
        ? {
            to: {
              id: data.pid,
              phoneNumber: data.phoneNumber,
              fullName: data.fullName
            }
          }
        : {}),
      ...(data.pid
        ? { from: { id: data.pid, name: data.fullName, type: "patient" } }
        : {}),
      ...(data.description
        ? { description: data.description }
        : { description: "" }),
      ...(data.url ? { url: data.url } : { url: {} }),
      ...(data.severityScale ? { severityScale: data.severityScale } : {}),
      ...(data.location ? { location: data.location } : {}),
      ...(data.timestamp ? { timestamp: data.timestamp } : {}),
      ...(data.type ? { type: data.type } : {}),
      ...(method === "create" ? { processing: true } : { processing: false })
    };
  }
  v.validate(profile, symptomsCollectionSchema, {
    throwAll: true,
    preValidateProperty
  });
  return rewriteSymptoms(profile);
};

export function documentSchema(data, method, documentType) {
  switch (documentType) {
    case "report":
      return documentModel(data, method);
    case "prescription":
      return documentModel(data, method);
    case "symptom":
      return symptomsModel(data, method);
    case "vitals":
      return vitalsModel(data, method);
    case "fitness":
      return fitnessModel(data, method);
    default:
      throw new Error("documentType misMatch");
  }
}

export function healthIdSchema(data, transactionId) {
  try {
    return {
      transactionId,
      password: data.password?.trim(),
      patientProfileDetails: {
        abhaAddress: data.healthId?.trim(),
        firstName: data.firstName?.trim() || "",
        middleName: data.middleName?.trim() || "",
        lastName: data.lastName?.trim() || "",
        dayOfBirth: data.date?.toString().padStart(2, "0") || "",
        monthOfBirth:
          monthsToInt(data.month)?.toString().padStart(2, "0") || "",
        yearOfBirth: data.year || "",
        gender: data.gender.slice(0, 1).toUpperCase(),
        stateCode: data.stateCode,
        stateName: stateOptionsWithCode.find((i) => i?.value === data.stateCode)
          ?.option,
        districtCode: data.districtCode,
        districtName: stateCodeWithDistricts?.[data.stateCode].find(
          (i) => i.value === data.districtCode
        )?.option,
        mobile: data.phoneNumber.replace("+91", ""),
        email: data.email?.trim() || "",
        pinCode: data.pinCode?.trim() || "",
        address:
          data.address
            ?.trim()
            // eslint-disable-next-line no-useless-escape
            ?.replace(/[^a-zA-Z0-9.,'()\/\s-]/g, " ")
            ?.replace(/\s+,/g, ",")
            ?.replace(/\s+/g, " ")
            ?.trim() || ""
      }
    };
  } catch (error) {
    return error;
  }
}

export function editProfileSchema(data) {
  const dob = new Date(data.dateOfBirth);
  return {
    profilePhoto: data.profilePhoto
      .replace(/^\s*data:image\/[a-zA-Z0-9+.-]+;base64,/, "")
      .trimStart(),
    firstName: data.name.first?.trim() || "",
    middleName: data.name.middle?.trim() || "",
    lastName: data.name.last?.trim() || "",
    dayOfBirth: dob.getDate().toString().padStart(2, "0"),
    monthOfBirth: (dob.getMonth() + 1).toString().padStart(2, "0"),
    yearOfBirth: dob.getFullYear().toString(),
    gender: getShortGender(data.gender),
    email: data.email?.trim() || "",
    mobile: data.phoneNumber?.trim() || "",
    address: data.address?.trim() || "",
    stateName: getStateNameWithStateCode(data.stateCode),
    districtName: getDistrictNameWithStateAndDistrictCode(
      data.stateCode,
      data.districtCode
    ),
    pinCode: data.pinCode,
    stateCode: data.stateCode,
    districtCode: data.districtCode
  };
}

const monthsToInt = (data) => {
  const months = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12
  };
  return months?.[data]?.toString()?.padStart(2, "0");
};
